import {Injectable} from '@angular/core';
import {HttpClientService, WebSocketClientService} from "inet-core";
import {APP_CHAT} from "./model/MessageConstants";
import {MessageContact} from "./model/MessageContact";
import {Observable, Subject} from "rxjs";
import {MessageChat} from "./model/MessageChat";
import {EnvelopBody, WebSocketEnvelop} from 'inet-core/src/websocket/websocket-abstract';

declare let iNet: any;

@Injectable({
  providedIn: 'platform'
})

export class ChatMessageService {
  private _socketObserver;
  private _messageSubject = new Subject<MessageChat>();
  private _unreadSubject = new Subject<number>();
  private _callInitContactListSubject = new Subject<any>();
  private _callModalToEditMessageGroup = new Subject<any>();

  contacts: MessageContact[] = [];
  messageReceived = this._messageSubject.asObservable();
  unreadMessageChange = this._unreadSubject.asObservable();

  constructor(private httpService: HttpClientService,
              public socketService: WebSocketClientService) {
    this._socketObserver = this.socketService.onMessage.subscribe((message: EnvelopBody) => {
      if (message.application === APP_CHAT) {
        this.updateLastMessage(message.sender, message);
        this._messageSubject.next(new MessageChat(message));
      }
    });

  }

  destroy(): void {
    this._socketObserver.unsubscribe();
    this._messageSubject.unsubscribe();
    this._unreadSubject.unsubscribe();
  }

  loadFriends() {
    return new Observable<MessageContact[]>((observer) => {
      this.httpService.getJSON(iNet.getPUrl('unifiedpush/list')).subscribe((data) => {
        observer.next((data || []).map((item: any) => new MessageContact(item)));
        observer.complete();
      });
    });
  }

  loadContacts(callback: Function) {
    if (this.contacts.length > 0) {
      callback(this.contacts);
    } else {
      this.loadFriends().subscribe(
        (data: MessageContact[]) => {
          this.contacts = data.filter((item) => item.usercode !== iNet.usercode);
          this.updateUnreadMessage();
          callback(this.contacts);
        }, () => callback([])
      );
    }
  }

  updateUnreadMessage() {
    let unread = 0;
    this.contacts.forEach((friend) => unread += friend.unread);
    this._unreadSubject.next(unread);
  }

  getContactByUserCode(usercode: string, callback: Function) {
    if (usercode === 'news-feed' || usercode === iNet.usercode) {
      return;
    }
    this.loadContacts((contacts: any) => {
      for (let i = 0; i < contacts.length; i++) {
        if (contacts[i].usercode === usercode) {
          return callback(contacts[i]);
        }
      }
      // contact not exist
      const contact = new MessageContact({usercode: usercode, fullname: usercode, unread: 1});
      this.contacts.unshift(contact);
      callback(contact);
    });
  }

  hasContact(usercode: string): boolean {
    for (let i = 0; i < this.contacts.length; i++) {
      if (this.contacts[i].usercode === usercode) {
        return true;
      }
    }
    return false;
  }

  send(username: string, message: string): Promise<boolean> {
    return this.socketService.send(username, message, APP_CHAT);
  }

  sendEnvelop(envelop: WebSocketEnvelop): Promise<boolean> {
    return this.socketService.sendEnvelop(envelop);
  }

  buildEnvelop(address: string, message: string, join?: string): WebSocketEnvelop {
    if (join) {
      return this.socketService.buildEnvelop(address, message, APP_CHAT, join);
    } else {
      return this.socketService.buildEnvelop(address, message, APP_CHAT);
    }
  }

  pushMessage(params: any, callback: Function) {
    const formData = this.httpService.convertToFormData(params);
    this.httpService.post(iNet.getPUrl('message/upload'), formData).subscribe(
      (data: EnvelopBody | any) => {
        let envelop: WebSocketEnvelop = this.socketService.buildEnvelop(params.alias, params.content, APP_CHAT);
        envelop.body.message = data.message;
        envelop.body.message.content = envelop.body.message.alert = params.content;
        envelop.body.uuid = data.uuid;
        this.socketService.sendEnvelop(envelop);
        callback(envelop.body);
      }
    );
  }

  loadMessages(params: any, callback: Function) {
    this.httpService.postJSON(iNet.getPUrl('message/query'), params).subscribe((data) => {
      data = data || {};
      data.items = data.items || [];
      data.total = data.total || 0;
      data.items.sort((a: any, b: any) => a.sent - b.sent);
      data.items = data.items.map((item: any) => new MessageChat(item));
      this.resetUnreadMessage(params['sender']);
      callback(data);
    });
  }

  updateLastMessage(usercode: string, message: EnvelopBody) {
    // Update last message, move contact to first
    this.getContactByUserCode(usercode, (contact: MessageContact) => {
      var index = this.contacts.indexOf(contact);
      if (index > -1) {
        this.contacts.splice(index, 1);
      }
      this.contacts.unshift(contact);
      let typeMessages = message.message.alert.split(":");
      if (typeMessages.length > 2) {
        if (typeMessages[0] === 'uri_message' && typeMessages[1] === 'video_conference') {
          contact.message = '<i class="fa fa-phone pr-1" aria-hidden="true"></i> Cuộc gọi thoại';
        } else {
          let __mesDisplay = this.revertMessageDisplay(message.message.alert) || '';
          if (typeMessages[0] === 'uri_message' && typeMessages[1] === 'link_preview') {
            contact.message = __mesDisplay;
          }
        }
      } else {
        contact.message = message.message.alert;
      }

      // contact.message = message.message.alert;

      // Not me send
      if (message.sender !== iNet.usercode) {
        contact.unread++;
        this.updateUnreadMessage();
      }
    });
  }

  decodeBase64(str: string) {
    return decodeURIComponent(escape(window.atob(str)));
  }

  resetUnreadMessage(username: string) {
    this.getContactByUserCode(username, (contact: any) => {
      contact.unread = 0;
      this.updateUnreadMessage();
    });
  }

  searchContacts(keyword: string, callback: Function) {
    this.loadContacts((contacts: MessageContact[]) => callback(this._filterUsers(keyword, contacts)));
  }

  private _filterUsers(keyword: string, contacts: MessageContact[]) {
    if (!keyword) {
      return contacts;
    }
    return contacts.filter((item) => item.isMatch(keyword));
  }

  revertMessage(message: string) {
    let __index = message.indexOf("uri_message") || 0;
    return message.substring(__index);
  }

  revertMessageDisplay(message: string) {
    let __index = message.indexOf("uri_message") || 0;
    return message.substring(0, __index);
  }

  reconnetSocket() {
    this.socketService.close();
  }

  checkJoinVideoCall(message: any) {
  }

  saveGroupMessageForm(groupChatForm: any): Observable<any> {
    return this.httpService.postJSON(iNet.getPUrl('conference/groupcreate'), groupChatForm)
  }

  getGroupMessageList(): Observable<any> {
    return this.httpService.postJSON(iNet.getPUrl('conference/groupcall'))
  }

  deleteGroupMessage(conferenceID: string): Observable<any> {
    return this.httpService.postJSON(iNet.getPUrl('conference/groupremove'), {conferenceID: conferenceID})
  }

  onReloadContactList(): Observable<any> {
    return this._callInitContactListSubject.asObservable();
  }

  reloadGroupList() {
    this.getGroupMessageList().subscribe(data => {
      this._callInitContactListSubject.next({groupList: data.items});
    });
  }

  onCallToEditMessageForm(): Observable<any> {
    return this._callModalToEditMessageGroup.asObservable();
  }

  callToEditMessageForm(messageForm: any) {
    this._callModalToEditMessageGroup.next({messageForm: messageForm});
  }
}
